/**
 * Persistent viewer package cache (IndexedDB).
 * Keyed by revisionId + manifest hash so a revision is downloaded only once.
 */

const DB_NAME = "okn-viewer-cache";
const STORE = "packages";
const VERSION = 1;

function openDb() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function withStore(mode, run) {
  try {
    const db = await openDb();
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, mode);
      const request = run(tx.objectStore(STORE));
      tx.oncomplete = () => resolve(request && "result" in request ? request.result : true);
      tx.onerror = () => reject(tx.error);
    });
  } catch {
    return null; // cache is a best-effort optimisation
  }
}

export const cacheKey = (revisionId, hash) => `${revisionId}:${hash || "v1"}`;

export async function readPackage(key) {
  return withStore("readonly", (store) => store.get(key));
}

export async function writePackage(key, payload) {
  return withStore("readwrite", (store) => store.put(payload, key));
}

/** Drop cached packages that belong to other revisions of the same project. */
export async function pruneOtherRevisions(revisionId) {
  return withStore("readwrite", (store) => {
    const request = store.getAllKeys();
    request.onsuccess = () => {
      request.result
        .filter((key) => typeof key === "string" && !key.startsWith(`${revisionId}:`))
        .forEach((key) => store.delete(key));
    };
    return null;
  });
}
